import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["disabled"]
const _hoisted_2 = {
  key: 0,
  class: "field"
}
const _hoisted_3 = { class: "control" }
const _hoisted_4 = {
  key: 1,
  class: "field"
}
const _hoisted_5 = { class: "control" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = ["value"]
const _hoisted_8 = {
  key: 0,
  class: "help is-danger"
}
const _hoisted_9 = {
  key: 2,
  class: "field"
}
const _hoisted_10 = { class: "control" }
const _hoisted_11 = { class: "select is-fullwidth" }

import { required } from '@vuelidate/validators';
import { FacebookApi } from './services/facebookAppApi';
import { messengerApi } from './services/messengerApi';
import { instagramApi } from './services/instagramApi';
import Toast from '@/shared/services/toast';
import { fbGetSdk, fbLogin, fbLogout, fbGetLoginStatus } from './services/messengerSdkWrapper';
import { FacebookPage, MessengerInstalledApp, InstallMessengerRequest } from './models';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { MessengerGraphApi } from '@/features/apps/messenger/models/messengerInstalledApp';

interface LoginScope {
  scope: string;
}

interface LoginOptions {
  messenger: LoginScope;
  instagram: LoginScope;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'messengerConfig',
  emits: ["loaded", "configSaved"],
  setup(__props, { emit: __emit }) {

const router = useRouter();
const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const currentApp = computed(() => store.getters.currentApp);

const selectedPage = ref<FacebookPage | null>(null);
const userPages = ref<FacebookPage[]>([]);
const messengerApp = ref<MessengerInstalledApp | null>(null);
const loginInProgress = ref(false);
const actionInProgress = ref(false);
const loadingPages = ref(false);
const connected = ref(false);

const validations = {
  selectedPage: { required },
};

const $v = useVuelidate(validations, { selectedPage });

const emit = __emit;

const loginOptions: LoginOptions = {
  messenger: {
    scope: 'business_management,pages_show_list,pages_read_engagement,pages_messaging,pages_manage_metadata',
  },
  instagram: {
    scope:
      'business_management,pages_show_list,pages_read_engagement,pages_manage_metadata,instagram_basic,instagram_manage_messages',
  },
};

let api: FacebookApi;

onBeforeMount(() => {
  api = currentApp.value.id === 'instagram' ? instagramApi : messengerApi;
  if (currentApp.value.isInstalled) {
    api
      .getInstalledApp(currentWorkspace.value.id)
      .then((fetchedApp: MessengerInstalledApp) => {
        loadFacebookLoginSdk(currentApp.value.id, fetchedApp.messengerGraphApi);

        if (fetchedApp.installed) {
          messengerApp.value = fetchedApp;
          selectedPage.value = {
            access_token: '',
            id: fetchedApp.pageId,
            name: fetchedApp.pageName,
          };
        }
      })
      .catch((err) => {
        // if it's not found, then we haven't installed the messenger app fully, so let them fill in the form
        if (err.status !== 404) {
          // show error state...
        }
      })
      .finally(() => {
        emit('loaded');
      });
  } else {
    emit('loaded');
  }
});

onBeforeUnmount(() => {
  if (!connected.value) {
    return;
  }
  fbLogout();
});

function loadFacebookLoginSdk(currentApp: string, apiDetails: MessengerGraphApi) {
  const appId = currentApp === 'instagram' ? apiDetails.instagramAppId : apiDetails.messengerAppId;
  fbGetSdk(appId, apiDetails.version).then(() => {
    fbGetLoginStatus().then((res: any) => {
      if (res.status === 'connected') {
        onConnected(res.authResponse.accessToken);
      }
    });
  });
}

function hasMessengerInstalledApp() {
  return messengerApp.value !== null;
}

function logout() {
  if (!connected.value) {
    return;
  }

  fbLogout().then(() => {
    fbGetLoginStatus().then((res: any) => {
      if (res.status !== 'connected') {
        connected.value = false;
      }
    });
  });
}

function login() {
  loginInProgress.value = true;
  const loginScope = loginOptions[currentApp.value.id as keyof LoginOptions].scope;
  fbLogin(loginScope)
    .then((res: any) => {
      if (res.status === 'connected') {
        onConnected(res.authResponse.accessToken);
      }
    })
    .finally(() => {
      loginInProgress.value = false;
    });
}

function onConnected(authToken: string) {
  connected.value = true;
  getFacebookPages(authToken);
}

function getFacebookPages(authToken: string) {
  loadingPages.value = true;
  api
    .getUserPages(authToken)
    .then((pages: FacebookPage[]) => {
      userPages.value = pages;
    })
    .finally(() => (loadingPages.value = false));
}

function save() {
  if (actionInProgress.value) {
    return;
  }

  if (!selectedPage.value) {
    return;
  }

  actionInProgress.value = true;

  const request = {
    installId: currentApp.value.installedAppId,
    workspaceId: currentWorkspace.value.id,
    pageId: selectedPage.value.id,
    pageName: selectedPage.value.name,
    pageAccessToken: selectedPage.value.access_token,
  } as InstallMessengerRequest;

  api
    .install(request)
    .then(() => emit('configSaved'))
    .catch((err) => Toast.showError(err.message))
    .finally(() => (actionInProgress.value = false));
}

function cancel() {
  router.push({ name: 'apps' });
}

return (_ctx: any,_cache: any) => {
  const _component_n_button = _resolveComponent("n-button")!
  const _component_n_button_group = _resolveComponent("n-button-group")!

  return (_openBlock(), _createElementBlock("fieldset", {
    disabled: hasMessengerInstalledApp()
  }, [
    (!hasMessengerInstalledApp())
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _cache[4] || (_cache[4] = _createElementVNode("label", { class: "label" }, "Log in to Facebook to authenticate your account", -1)),
          _createElementVNode("div", _hoisted_3, [
            (!connected.value)
              ? (_openBlock(), _createElementBlock("a", {
                  key: 0,
                  class: _normalizeClass(["button is-link", { 'is-loading': loginInProgress.value }]),
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (login()))
                }, "Authenticate with Facebook", 2))
              : _createCommentVNode("", true),
            (connected.value)
              ? (_openBlock(), _createElementBlock("a", {
                  key: 1,
                  class: _normalizeClass(["button is-success", { 'is-loading': loginInProgress.value }])
                }, "Authenticated", 2))
              : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true),
    (!hasMessengerInstalledApp())
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _cache[5] || (_cache[5] = _createElementVNode("label", { class: "label" }, "Facebook Page", -1)),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", {
              class: _normalizeClass(["select is-fullwidth", { 'is-loading': loadingPages.value }])
            }, [
              _withDirectives(_createElementVNode("select", {
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedPage).value = $event)),
                disabled: !connected.value || loadingPages.value
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(userPages.value, (page) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: page.id,
                    value: page
                  }, _toDisplayString(page.name), 9, _hoisted_7))
                }), 128))
              ], 8, _hoisted_6), [
                [_vModelSelect, selectedPage.value]
              ])
            ], 2),
            (_unref($v).selectedPage.$invalid && _unref($v).selectedPage.$dirty)
              ? (_openBlock(), _createElementBlock("p", _hoisted_8, " The Facebook Page is required "))
              : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true),
    (hasMessengerInstalledApp())
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _cache[6] || (_cache[6] = _createElementVNode("label", { class: "label" }, "Facebook Page", -1)),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("select", null, [
                _createElementVNode("option", null, _toDisplayString(messengerApp.value?.pageName), 1)
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(_component_n_button_group, null, {
      default: _withCtx(() => [
        _createVNode(_component_n_button, {
          variant: "primary",
          onClick: _cache[2] || (_cache[2] = ($event: any) => (save())),
          disabled: _unref($v).$invalid,
          loading: actionInProgress.value
        }, {
          default: _withCtx(() => _cache[7] || (_cache[7] = [
            _createTextVNode("Submit")
          ])),
          _: 1
        }, 8, ["disabled", "loading"]),
        _createVNode(_component_n_button, {
          onClick: _cache[3] || (_cache[3] = ($event: any) => (cancel())),
          variant: "primary--outline"
        }, {
          default: _withCtx(() => _cache[8] || (_cache[8] = [
            _createTextVNode("Cancel")
          ])),
          _: 1
        })
      ]),
      _: 1
    })
  ], 8, _hoisted_1))
}
}

})